<template>
  <div class="w-100 identity-configuration grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-palette'"
      :section-title="$t('configuration.sections.identity')"
      :show-display-options="false"
    />
    <div
      v-if="corporativeIdentityModel"
      class="configuration-box d-flex flex-column justify-content-between"
    >
      <div>
        <div class="py-4 px-5">
          <span class="ap-title ap-title__section"><i
            class="far fa-hospital mr-3"
            aria-hidden="true"
          />{{ $t('configuration.identitySection.identifiersAndLogos') }}</span>
          <b-row class="mt-3">
            <laboratory-configuration-info
              :data="corporativeIdentityModel"
            />
            <b-col cols="auto">
              <div class="vertical-line" />
            </b-col>
            <b-col>
              <b-row>
                <laboratory-configuration-image-uploader
                  :id="'logo'"
                  :action-title="'configuration.identitySection.navbarLogo'"
                  :get-logo="getLogo"
                  :set-logo="setLogo"
                  :delete-logo="deleteLogo"
                  :update-logo="true"
                  :message-upload="$t('configuration.toastMessages.identityConfigurationLaboratoryUploadLogo')"
                  :message-delete="$t('configuration.toastMessages.identityConfigurationLaboratoryLogoDeleted')"
                />
                <laboratory-configuration-image-uploader
                  :id="'logo-reports'"
                  :action-title="'configuration.identitySection.reportsLogo'"
                  :get-logo="getLogoReports"
                  :set-logo="setLogoReports"
                  :delete-logo="deleteLogoReports"
                  :message-upload="$t('configuration.toastMessages.identityConfigurationLaboratoryUploadLogoReports')"
                  :message-delete="$t('configuration.toastMessages.identityConfigurationLaboratoryLogoReportsDeleted')"
                />
              </b-row>
            </b-col>
          </b-row>
        </div>
        <div class="py-4 px-5">
          <span class="ap-title ap-title__section"><i
            class="far fa-map-marker-alt mr-3"
            aria-hidden="true"
          />{{ $t('configuration.identitySection.location') }}</span>
          <common-address-info :data="corporativeIdentityModel.address" />
        </div>
        <div class="py-4 px-5">
          <span class="ap-title ap-title__section"><i
            class="far fa-envelope mr-3"
            aria-hidden="true"
          />{{ $t('configuration.identitySection.contact') }}</span>
          <common-contact-info :data="corporativeIdentityModel.contactInformation" />
          <b-row class="mt-3">
            <b-col cols="12">
              <div class="mt-3">
                <div class="form-label">
                  <label>{{ $t('configuration.identitySection.notes') }}</label>
                </div>
                <b-form-textarea
                  v-model="corporativeIdentityModel.address.notes"
                  rows="2"
                  no-resize
                />
              </div>
            </b-col>
            <b-col
              cols="12"
              class="mt-3 d-flex flex-sm-column flex-md-row justify-content-end align-items-sm-end align-items-md-center"
            >
              <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                <span class="mr-2">{{ $t('configuration.modal.emailNotifications') }}</span>
                <input
                  id="toggleMail"
                  v-model="corporativeIdentityModel.contactInformation.emailNotificationEnabled"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggleMail"
                  class="switch mb-0"
                />
              </div>
              <div class="switch-btn mt-1 ml-5 p-0 d-flex align-items-center">
                <span class="mr-2">{{ $t('configuration.modal.smsNotifications') }}</span>
                <input
                  id="toggleSms"
                  v-model="corporativeIdentityModel.contactInformation.smsNotificationEnabled"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggleSms"
                  class="switch mb-0"
                />
              </div>
              <div class="switch-btn mt-1 ml-5 p-0 d-flex align-items-center">
                <span class="mr-2">{{ $t('configuration.actions.active') }}</span>
                <input
                  id="toggleCenterActive"
                  v-model="corporativeIdentityModel.active"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggleCenterActive"
                  class="switch mb-0"
                />
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <div class="fixed-footer component-bg d-flex justify-content-end px-5 py-3">
        <b-button
          size="sm"
          class="btn--primary"
          :disabled="!!errors.count() || !isFormDirty"
          @click="saveIdentityConfigurationLab"
        >
          <i
            class="fal fa-save mr-2"
            aria-hidden="true"
          />
          {{ $t('global.save') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import laboratoryConfigurationServices from '@/api/resources/configuration/laboratory/corporateIdentity/laboratoryConfigurationServices'
import CommonAddressInfo from '@/components/common/CommonAddressInfo'
import CommonContactInfo from '@/components/common/CommonContactInfo'
import LaboratoryConfigurationInfo from '@/modules/configurationModule/detail/laboratory/identity/LaboratoryConfigurationInfo'
import LaboratoryConfigurationImageUploader from '@/modules/configurationModule/detail/laboratory/identity/LaboratoryConfigurationImageUploader'
import attachmentTypeService, { ATTACHMENT_TYPES } from '@/api/resources/attachment/attachmentTypeService'
import { uiComponentCustomConfigService } from '@/api/services/common/ui-component/custom-config/ui-component-custom-config.service'
import sessionStore from '@/session/sessionStore'
import { store } from '@/store/store'

export default {
    name: 'LaboratoryConfiguration',
    components: {
        ConfigurationSectionHeader,
        CommonAddressInfo,
        CommonContactInfo,
        LaboratoryConfigurationInfo,
        LaboratoryConfigurationImageUploader
    },
    provide() {
        return {
            formValidator: this.$validator
        }
    },
    data() {
        return {
            labId: this.$store.getters.labId,
            corporativeIdentityModel: null,
            centers: {
                selected: {},
                data: []
            },
            entity: 'LABORATORY'
        }
    },
    computed: {
        isFormDirty() {
            return Object.keys(this.fields).some(key => this.fields[key].dirty)
        }
    },
    created() {
        this.getInfoLaboratory()
    },
    methods: {
        async getInfoLaboratory() {
            this.updateLoading(true)
            const [err, data] = await this.toCall(laboratoryConfigurationServices.getLaboratoryConfiguration(this.labId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
            } else {
                this.corporativeIdentityModel = data
            }
        },
        async getAttachments() {
            const [err, data] = await this.toCall(laboratoryConfigurationServices.getLaboratoryConfigurationAttachments(this.labId))
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return []
            } else {
                return data
            }
        },
        mapIdentityConfigurationToRequest() {
            return {
                address: {
                    address: this.corporativeIdentityModel.address.address,
                    countryId: this.corporativeIdentityModel.address.country.id,
                    municipalityId: this.corporativeIdentityModel.address.municipality.id,
                    notes: this.corporativeIdentityModel.address.notes,
                    postalCode: this.corporativeIdentityModel.address.postalCode,
                    provinceId: this.corporativeIdentityModel.address.province.id
                },
                centerId: this.corporativeIdentityModel.center.id,
                contactInformation: {
                    email: this.corporativeIdentityModel.contactInformation.email,
                    emailNotificationEnabled: this.corporativeIdentityModel.contactInformation.emailNotificationEnabled,
                    emailPass: this.corporativeIdentityModel.contactInformation.emailPass,
                    mainPhone: this.corporativeIdentityModel.contactInformation.mainPhone,
                    otherPhone1: this.corporativeIdentityModel.contactInformation.otherPhone1,
                    otherPhone2: this.corporativeIdentityModel.contactInformation.otherPhone2,
                    smsNotificationEnabled: this.corporativeIdentityModel.contactInformation.smsNotificationEnabled
                },
                labActive: this.corporativeIdentityModel.active,
                labExternalId: this.corporativeIdentityModel.externalId,
                labName: this.corporativeIdentityModel.name
            }
        },
        async updateUserDefaultAccessDefaultLab(updatedLaboratoriesConfigurationResponse) {
            const userDefaultAccessUiComponent =
                await this.$_toCallUnique(uiComponentCustomConfigService.get(sessionStore.state.userDefaultAccessUiComponentCode))
            if (userDefaultAccessUiComponent) {
                const defaultLabConfigurationToUpdate = updatedLaboratoriesConfigurationResponse.find(updatedLaboratory => updatedLaboratory.laboratory.labId === userDefaultAccessUiComponent.config.defaultLab.labId)
                if (defaultLabConfigurationToUpdate) {
                    const userDefaultAccessUiComponentConfig = {
                        defaultLab: defaultLabConfigurationToUpdate.laboratory,
                        defaultLabModules: sessionStore.mapDefaultLabModules(store.getters.laboratoriesConfiguration)
                    }
                    await sessionStore.saveUserDefaultAccess(userDefaultAccessUiComponentConfig)
                }
            }
        },
        async saveIdentityConfigurationLab() {
            const request = this.mapIdentityConfigurationToRequest()
            const [err] = await this.$_toCall(laboratoryConfigurationServices.updateLaboratoryConfiguration(this.labId, request))
            if (!err) {
                await this.updateUserDefaultAccessDefaultLab(store.getters.laboratoriesConfiguration)
                this.showToastSuccessGlobal(this.$t('configuration.toastMessages.identityConfigurationLaboratoryFormSaved'))
            }
        },
        async getLogo() {
            const [err, data] = await this.toCall(laboratoryConfigurationServices.getLaboratoryConfigurationLogo(this.labId))
            if (err) {
                return null
            } else {
                return data
            }
        },
        async getLogoReports() {
            const [err, data] = await this.toCall(laboratoryConfigurationServices.getLaboratoryConfigurationLogoReports(this.labId))
            if (err) {
                return null
            } else {
                return data
            }
        },
        async setLogo(request) {
            this.updateLoading(true)
            const [err, data] = await this.toCall(laboratoryConfigurationServices.updateLaboratoryConfigurationLogo(this.labId, request))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return null
            } else {
                return data
            }
        },
        async setLogoReports(request) {
            this.updateLoading(true)
            const [err, data] = await this.toCall(laboratoryConfigurationServices.updateLaboratoryConfigurationLogoReports(this.labId, request))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return null
            } else {
                return data
            }
        },
        async deleteLogo() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(attachmentTypeService.getAttachmentType(this.entity, ATTACHMENT_TYPES.LABORATORY_LOGO))
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            await this.delete(response && response.atyId)
            this.updateLoading(false)
        },
        async deleteLogoReports() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(attachmentTypeService.getAttachmentType(this.entity, ATTACHMENT_TYPES.LABORATORY_REPORT_LOGO))
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            await this.delete(response && response.atyId)
            this.updateLoading(false)
        },
        async delete(attachmentId) {
            const attachments = await this.getAttachments()
            const foundAttachment = attachments && attachments.find((attach) => attach && attach.atyId === attachmentId)
            const [err] = await this.toCall(laboratoryConfigurationServices.deleteLaboratoryConfigurationLogo(this.labId, foundAttachment && foundAttachment.attachmentId))
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
            }
        }
    }
}
</script>

<style lang="scss">
.identity-configuration .line-separator,
.identity-configuration #pop-configuration-shortcuts,
.identity-configuration #pop-configuration-voice {
    display: none;
}
.identity-configuration .element-row {
    border-bottom: 1px solid vt-color(card-border);
}
.identity-configuration .element-row:hover {
    background: vt-color(light-primary);
}
.identity-configuration .element-row .element-name {
    color: vt-color(dark-text);
}
.identity-configuration .vertical-line {
    width: 1px;
    height: 100%;
    background: vt-color(general-border);
}
.identity-configuration .logo-box {
    background: vt-color(primary);
    width: 100%;
    max-height: 200px;
    overflow: hidden;
}
.identity-configuration .logo-box.print {
    background: vt-color(light-grey, .6);
}
.identity-configuration .configuration-box {
    position: relative;
}
.identity-configuration .fixed-footer {
    background: vt-color(white-light);
    position: sticky;
    bottom: 0;
}
</style>
